import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Tooltip } from "antd";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import classnames from "classnames";
import styles from "./ReferencesList.module.less";
import type { IThinkReference } from "../ThinkingProcessV2";
import FeedbackModal from "@components/FeedbackModal";
import { getReferenceToolIcon, type IconState } from "../../utils";
import { useDebouncedCallback } from "@hooks/useDebouncedCallback";
import { useUbcSend } from "@hooks/useUbcSend";
import { useChatStore } from "@store/chatStore";

// 引用图标
let iconClose = "https://muzhi-public-pic.cdn.bcebos.com/100115353170";
let iconSummarize = "https://muzhi-public-pic.cdn.bcebos.com/100115353168";
let iconArrowDown = "https://muzhi-public-pic.cdn.bcebos.com/100115338615";

/**
 * 文献列表组件Props
 */
interface IReferencesListProps {
    docs: IThinkReference[];
    totalCount: number;
    messageId: string;
    segmentIdx: string;
    anchorIndex: string | null;
    paragraphIndex: number | null;
    triggerTimestamp?: number | null;
    onClose?: () => void;
    onFeedback?: (
        messageId: string,
        segmentIdx: string,
        refId: string,
        type: "like" | "unlike",
        tags?: string[],
        content?: string
    ) => void;
    onCancelFeedback?: (messageId: string, segmentIdx: string, refId: string) => void;
    /** 点击跳转回调，用于合规校验 */
    onNavigate?: (doc: IThinkReference) => void;
    /** 流式输出中禁用点赞/点踩 */
    sseLoading?: boolean;
}

/**
 * 单个文献项组件
 */
interface IReferenceItemProps {
    doc: IThinkReference;
    messageId: string;
    segmentIdx: string;
    defaultExpanded?: boolean;
    onFeedback?: (
        messageId: string,
        segmentIdx: string,
        refId: string,
        type: "like" | "unlike",
        tags?: string[],
        content?: string
    ) => void;
    onCancelFeedback?: (messageId: string, segmentIdx: string, refId: string) => void;
    isActive?: boolean; // 当前是否为活动项（用于锚点定位）
    paragraphIndex?: number; // 段落索引，用于指定显示 content_list 中的哪一段，默认为 0
    triggerTimestamp?: number | null; // 触发器时间戳，确保相同参数也能触发滚动
    /** 点击跳转回调，用于合规校验 */
    onNavigate?: (doc: IThinkReference) => void;
    /** 流式输出中禁用点赞/点踩 */
    sseLoading?: boolean;
}

const ReferenceItem: React.FC<IReferenceItemProps> = ({
    doc,
    messageId,
    segmentIdx,
    defaultExpanded = false,
    onFeedback,
    onCancelFeedback,
    isActive = false,
    paragraphIndex = 0,
    triggerTimestamp,
    onNavigate,
    sseLoading = false,
}) => {
    const { ubcSend } = useUbcSend();
    const [isContentExpanded, setIsContentExpanded] = useState(defaultExpanded);
    const refItemRef = useRef<HTMLDivElement>(null);
    const sessionId = useChatStore((state) => state.currentSessionId);
    // 用 ref 记录是否已上报，避免 SSE 流式重渲染时重复发送
    const hasReportedRef = useRef(false);

    // 曝光埋点：组件挂载后上报一次，用 ref 保证幂等
    useEffect(() => {
        if (hasReportedRef.current) return;
        if (!doc.tool_tip?.tools || doc.tool_tip.tools.length === 0) return;

        hasReportedRef.current = true;
        doc.tool_tip.tools.forEach((item) => {
            ubcSend({
                type: "view",
                value: `reference_${item.type}`,
                ext: {
                    product_info: {
                        session_id: sessionId,
                        message_id: messageId,
                        reference_id: doc.id,
                    },
                },
            });
        });
        // 只在 tool_tip 首次有值时触发，之后不再重复
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc.tool_tip?.tools?.length]);

    // 监听 defaultExpanded 变化，更新展开状态
    useEffect(() => {
        setIsContentExpanded(defaultExpanded);
    }, [defaultExpanded]);

    // 监听 isActive、展开状态和 triggerTimestamp 变化，在展开后再滚动到锚点位置
    // triggerTimestamp 确保即使参数相同也能重新触发滚动
    useEffect(() => {
        if (isActive && isContentExpanded && refItemRef.current) {
            // 使用 setTimeout 确保内容渲染完成后再滚动
            const timer = setTimeout(() => {
                refItemRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start", // 将元素定位到可视区域的顶部
                });
            }, 100); // 延迟 100ms，等待内容展开动画完成

            return () => clearTimeout(timer);
        }
    }, [isActive, isContentExpanded, triggerTimestamp]);
    const [hoveredTool, setHoveredTool] = useState<string | null>(null);
    const [modalState, setModalState] = useState<{ isOpen: boolean; type: "like" | "unlike" } | null>(null);
    // 缓存弹窗打开时的 popupConfig，防止点击后 doc 更新导致 popupConfig 丢失
    const [cachedPopupConfig, setCachedPopupConfig] = useState<{ like?: any; unlike?: any }>({});

    // 从 IThinkReference 提取显示数据，根据 paragraphIndex 显示对应段落
    const content = useMemo(
        () => (doc.content_list?.[paragraphIndex] || "").replace(/\\n/g, "\n"),
        [doc.content_list, paragraphIndex]
    );

    // 从后端 reference.feature 协议转换后的 tool_tip 获取点赞/点踩状态
    const referenceTools = doc.tool_tip?.tools || [];
    const likeStatus = referenceTools.find((t) => t.type === "like")?.status || 0;
    const unlikeStatus = referenceTools.find((t) => t.type === "unlike")?.status || 0;
    const likePopupConfig = referenceTools.find((t) => t.type === "like")?.popupConfig;
    const unlikePopupConfig = referenceTools.find((t) => t.type === "unlike")?.popupConfig;
    const feedbackFragmentId = doc.tool_tip?.fragment_id || doc.id;

    // 获取图标状态
    const getIconState = useCallback(
        (toolType: "like" | "unlike"): IconState => {
            const status = toolType === "like" ? likeStatus : unlikeStatus;
            if (status === 1) return "selected";

            // 当弹窗打开时，强制设置为 hover 态
            if (modalState?.isOpen && modalState.type === toolType) return "hover";

            if (hoveredTool === toolType) return "hover";
            return "default";
        },
        [likeStatus, unlikeStatus, hoveredTool, modalState]
    );

    // 点赞/点踩处理（防抖）
    const handleToolClick = useDebouncedCallback(
        (toolType: "like" | "unlike") => {
            if (sseLoading) return;
            const isSelected = (toolType === "like" ? likeStatus : unlikeStatus) === 1;

            // 点击埋点
            ubcSend({
                type: "clk",
                value: isSelected ? `reference_${toolType}_cancel` : `reference_${toolType}`,
                ext: {
                    product_info: {
                        session_id: sessionId,
                        message_id: messageId,
                        reference_id: doc.id,
                    },
                },
            });

            // 如果已经选中，触发取消操作
            if (isSelected) {
                onCancelFeedback?.(messageId, segmentIdx, feedbackFragmentId);
                return;
            }

            // 立即调用 API（不带 tags 和 content）
            onFeedback?.(messageId, segmentIdx, feedbackFragmentId, toolType);

            // 缓存当前 popupConfig 并打开弹窗
            setCachedPopupConfig({ like: likePopupConfig, unlike: unlikePopupConfig });
            setModalState({ isOpen: true, type: toolType });
        },
        1000,
        [
            sseLoading,
            likeStatus,
            unlikeStatus,
            onFeedback,
            onCancelFeedback,
            messageId,
            segmentIdx,
            doc.id,
            feedbackFragmentId,
            sessionId,
            content,
        ]
    );

    // 弹窗提交
    const handleFeedbackSubmit = useCallback(
        (type: "like" | "unlike", selectedValues: string[], inputValue: string) => {
            onFeedback?.(messageId, segmentIdx, feedbackFragmentId, type, selectedValues, inputValue);
            setModalState(null);
            ubcSend({
                type: "clk",
                value: `feedback_submit_reference_${type}`,
                ext: {
                    product_info: {
                        session_id: sessionId,
                        message_id: messageId,
                        reference_id: doc.id,
                        selectedValue: selectedValues.join(","),
                        otherText: inputValue,
                    },
                },
            });
        },
        [messageId, segmentIdx, doc.id, feedbackFragmentId, sessionId, onFeedback]
    );

    // 跳转到文献链接 - 支持合规校验
    const handleDocLinkClick = useCallback(() => {
        ubcSend({
            type: "clk",
            value: "reference_doc_link",
            ext: {
                product_info: {
                    session_id: sessionId,
                    message_id: messageId,
                    reference_id: doc.id,
                    doc_link: doc.url,
                },
            },
        });
        if (onNavigate) {
            onNavigate(doc);
        } else if (doc.url) {
            window.open(doc.url, "_blank");
        }
    }, [doc, messageId, sessionId, onNavigate]);

    // 渲染反馈按钮
    const renderFeedbackButton = useCallback(
        (toolType: "like" | "unlike") => {
            const iconState = getIconState(toolType);
            const isSelected = (toolType === "like" ? likeStatus : unlikeStatus) === 1;
            const tooltipTitle = toolType === "like" ? "满意" : "不满意";

            const handleClick = () => {
                setHoveredTool(null); // 点击时关闭 Tooltip
                handleToolClick(toolType);
            };

            return (
                <Tooltip
                    title={tooltipTitle}
                    placement="top"
                    arrow={false}
                    open={hoveredTool === toolType ? undefined : false}
                >
                    <button
                        className={`${styles.actionBtn} ${isSelected ? styles.selected : ""} ${sseLoading ? styles.disabled : ""}`}
                        onClick={handleClick}
                        onMouseEnter={() => setHoveredTool(toolType)}
                        onMouseLeave={() => setHoveredTool(null)}
                        disabled={sseLoading}
                    >
                        <img
                            src={getReferenceToolIcon(toolType, iconState)}
                            alt={toolType === "like" ? "点赞" : "点踩"}
                        />
                    </button>
                </Tooltip>
            );
        },
        [sseLoading, getIconState, likeStatus, unlikeStatus, hoveredTool, handleToolClick]
    );

    const contentClick = useCallback(() => {
        setIsContentExpanded(!isContentExpanded);
        if (!isContentExpanded) {
            ubcSend({
                type: "clk",
                value: "reference_quote_toggle",
                ext: {
                    product_info: {
                        session_id: sessionId,
                        message_id: messageId,
                        reference_id: doc.id,
                    },
                },
            });
        }
    }, [doc.id, isContentExpanded, messageId, sessionId]);

    return (
        <div ref={refItemRef} className={styles.referenceItem}>
            {/* 文献头部信息 */}
            <div className={styles.referenceHeader}>
                <div className={styles.titleRow}>
                    <div className={classnames(styles.titleContent, { [styles.noLink]: !doc.url })}>
                        <span className={styles.index}>{doc.index}.</span>
                        <span className={styles.typeTag}>{doc.source || "文献"}</span>
                        <span
                            className={styles.title}
                            onClick={handleDocLinkClick}
                            style={{ cursor: doc.url ? "pointer" : "default" }}
                        >
                            {doc.title}
                        </span>
                    </div>
                </div>

                {/* 作者和来源信息 */}
                {doc.desc_list && doc.desc_list.length > 0 && (
                    <div className={styles.metaInfo}>
                        {doc.desc_list.map((desc, idx) => (
                            <div key={idx} className={styles.descItem}>
                                {desc}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 引用段落控制区 */}
            <div className={styles.quoteActions}>
                <div className={styles.quoteToggle} onClick={contentClick}>
                    <img src={iconSummarize} alt="引用段落" className={styles.icon} />
                    <span className={styles.text}>引用段落</span>
                    <img
                        src={iconArrowDown}
                        alt={isContentExpanded ? "收起" : "展开"}
                        className={styles.arrowIcon}
                        style={{
                            transform: isContentExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                    />
                </div>

                <div className={styles.feedbackActions}>
                    <FeedbackModal
                        open={modalState?.isOpen === true && modalState.type === "like"}
                        type="like"
                        configType="reference"
                        messageId={messageId}
                        popupConfig={cachedPopupConfig.like || likePopupConfig}
                        onClose={() => setModalState(null)}
                        onSubmit={handleFeedbackSubmit}
                    >
                        {renderFeedbackButton("like")}
                    </FeedbackModal>
                    <FeedbackModal
                        open={modalState?.isOpen === true && modalState.type === "unlike"}
                        type="unlike"
                        configType="reference"
                        messageId={messageId}
                        popupConfig={cachedPopupConfig.unlike || unlikePopupConfig}
                        onClose={() => setModalState(null)}
                        onSubmit={handleFeedbackSubmit}
                    >
                        {renderFeedbackButton("unlike")}
                    </FeedbackModal>
                </div>
            </div>

            {/* 引用内容 */}
            {isContentExpanded && content && (
                <div
                    className={styles.quoteContent}
                    onClick={handleDocLinkClick}
                    style={{ cursor: doc.url ? "pointer" : "default" }}
                >
                    <div className={styles.quoteContentInner}>
                        <ReactMarkdown
                            remarkPlugins={[
                                [remarkGfm, { singleTilde: false }], // 禁用单波浪线删除线，只支持 ~~text~~
                                remarkBreaks,
                                remarkMath,
                            ]}
                            rehypePlugins={[rehypeRaw, rehypeKatex]}
                        >
                            {content}
                        </ReactMarkdown>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * 引用文献列表组件 - 新版设计
 * 移动端样式，宽度320px
 */
const ReferencesList: React.FC<IReferencesListProps> = ({
    docs,
    totalCount,
    messageId,
    segmentIdx,
    anchorIndex,
    paragraphIndex,
    triggerTimestamp,
    onClose,
    onFeedback,
    onCancelFeedback,
    onNavigate,
    sseLoading,
}) => {
    return (
        <div className={styles.container}>
            {/* 头部 */}
            <div className={styles.header}>
                <h2 className={styles.headerTitle}>引用文献（{totalCount}）</h2>
                <img src={iconClose} alt="关闭" className={styles.closeIcon} onClick={onClose} />
            </div>

            {/* 文献列表 */}
            <div className={styles.listContent}>
                {docs.map((doc, index) => {
                    // 根据 anchorIndex 判断是否默认展开引用段落
                    // anchorIndex 是引用编号（如 "1"），doc.index 也是引用编号（数字）
                    const shouldExpand = anchorIndex !== null && String(doc.index) === anchorIndex;

                    return (
                        <React.Fragment key={index}>
                            <ReferenceItem
                                doc={doc}
                                messageId={messageId}
                                segmentIdx={segmentIdx}
                                defaultExpanded={shouldExpand}
                                onFeedback={onFeedback}
                                onCancelFeedback={onCancelFeedback}
                                isActive={shouldExpand} // 只有当匹配 anchorIndex 时才激活
                                paragraphIndex={shouldExpand && paragraphIndex !== null ? paragraphIndex : 0} // 匹配时使用指定索引，否则默认为 0
                                triggerTimestamp={triggerTimestamp}
                                onNavigate={onNavigate}
                                sseLoading={sseLoading}
                            />
                            {/* 分隔线 */}
                            {index < docs.length - 1 && <div className={styles.divider} />}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default ReferencesList;
